import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    TextField,
    Alert,
    Autocomplete,
    Paper,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Send } from '@mui/icons-material';
import { GroundStationManager } from 'models';

import { useAppDispatch, useAppSelector } from 'hooks';
import {
    getGroundStationCommandTypes,
    sendGroundStationCommand,
} from 'state/groundstations';

interface Props {
    groundstation: GroundStationManager.GroundStation;
    disabled?: boolean;
}

const SendCommandForm: React.FunctionComponent<Props> = ({
    groundstation,
    disabled,
}) => {
    const dispatch = useAppDispatch();

    const { tokens } = useAppSelector((root) => root.auth);
    const { commandTypes, latestCommands } = useAppSelector(
        (root) => root.groundstations,
    );

    const [command, setCommand] =
        useState<GroundStationManager.CommandType | null>(null);

    const [sentCommand, setSentCommand] =
        useState<GroundStationManager.API.Command>();

    const [params, setParams] = useState<
        Record<string, string | number | boolean>
    >({});

    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const commandParameters = command?.parameters ?? [];

    const isValid = !!command;

    const latestSentCommand =
        sentCommand != null ? latestCommands[sentCommand.id] : null;

    const sendCommand = async () => {
        if (!command) return;

        setSending(true);
        setError('');
        setMessage('');

        try {
            const results = await dispatch(
                sendGroundStationCommand({
                    id: groundstation.id,
                    command: command.name,
                    params,
                }),
            );

            if (results.error) setError(results.error);

            if (results.success) setMessage('Command sent successfully');

            if (results.command) setSentCommand(results.command);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (tokens.status === 'finished' && commandTypes.status === 'idle') {
            dispatch(getGroundStationCommandTypes());
        }
    }, [tokens.status]);

    return (
        <Paper
            sx={{
                display: 'grid',
                gap: 1,
                p: 1,
                gridTemplateRows: 'auto auto auto auto 1fr auto',
            }}
        >
            <Typography variant="h6">Send a Command</Typography>
            <Typography variant="subtitle2">
                Allows sending an Arbitrary Command out of all supported
                commands.
            </Typography>
            {error && (
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {message && !latestSentCommand && (
                <Alert severity="success" onClose={() => setMessage('')}>
                    {message}
                </Alert>
            )}
            {latestSentCommand && latestSentCommand.status === 'FINISHED' && (
                <Alert
                    severity="success"
                    onClose={() => {
                        setSentCommand(undefined);
                        setMessage('');
                    }}
                >
                    {latestSentCommand.results
                        ? `Ground Station Command Results: ${JSON.stringify(
                              latestSentCommand.results,
                              null,
                              2,
                          )}`
                        : 'Ground Station Completed the Command'}
                </Alert>
            )}
            {latestSentCommand && latestSentCommand.status === 'ERROR' && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setSentCommand(undefined);
                        setMessage('');
                    }}
                >
                    {latestSentCommand.error ??
                        'Ground Station was unable to complete the command'}
                </Alert>
            )}
            {/* Select desired Command */}
            <Autocomplete<GroundStationManager.CommandType>
                value={command}
                options={commandTypes.object ?? []}
                onChange={(event, value) => setCommand(value)}
                getOptionLabel={(option) => option.name}
                getOptionDisabled={(commandType) =>
                    !groundstation.supportedCommands.includes(commandType.id)
                }
                placeholder="Enter a command to send"
                fullWidth
                renderInput={(textParams) => (
                    <TextField {...textParams} label="Command" />
                )}
            />
            <Typography variant="body1">{command?.summary}</Typography>
            {/* Section for Command Parameters */}
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                {commandParameters.map((commandParam) => (
                    <TextField
                        key={commandParam}
                        label={commandParam}
                        value={params[commandParam] ?? ''}
                        onChange={(event) =>
                            setParams({
                                ...params,
                                [commandParam]: event.target.value,
                            })
                        }
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                    variant="contained"
                    onClick={sendCommand}
                    loading={sending}
                    disabled={
                        !isValid || disabled || !groundstation.connectionId
                    }
                    endIcon={<Send />}
                >
                    Send Command
                </LoadingButton>
            </Box>
        </Paper>
    );
};

SendCommandForm.displayName = 'SendCommandForm';
export default SendCommandForm;
